// test new file
import { useState, useEffect } from "react";
import { Plus, Trash2 } from "lucide-react";
import { FaEdit } from "react-icons/fa";
import { toast } from "react-toastify";
import { getChannelRules, deleteChannelRule } from "../api/costingSettingsApi";
import ChannelRuleModal from "../modals/ChannelRuleModal";

const COLUMNS = [
  { key: "priceLevel",              label: "Price Level" },
  { key: "targetNetMargin",         label: "Target Net Margin %" },
  { key: "marketingPercent",        label: "Marketing %" },
  { key: "useFreight",              label: "Use Freight?" },
  { key: "crossBorder",             label: "Use Cross-Border?" },
  { key: "ticketingPerUnit",        label: "Ticketing $/Unit" },
  { key: "assortingPerUnit",        label: "Assorting $/Unit" },
  { key: "royaltyRegion",           label: "Royalty Region" },
  { key: "srpRoundingRule",         label: "SRP Rounding Rule" },
  { key: "priceEnding",             label: "Price Ending Rule" },
  { key: "commissionType",          label: "Commission Type" },
  { key: "commissionPercent",       label: "Commission %" },
  { key: "freightAllowancePercent", label: "Freight Allowance %" },
  { key: "fixedUsdFx",              label: "Fixed USD FX vs CAD" },
  { key: "usdRoundTo",              label: "USD Round To" },
];

export default function ChannelRules() {
  const [rules, setRules]           = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  // null  → modal closed
  // {}    → modal open for Create
  // rule  → modal open for Edit
  const [editingRule, setEditingRule] = useState(null);

  useEffect(() => { fetchRules(); }, []);

  async function fetchRules() {
    setLoading(true);
    setError(null);
    try {
      const json = await getChannelRules();
      if (!json.success) throw new Error(json.message || "Failed to fetch channel rules");
      setRules(json.data || []);
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        "Failed to fetch channel rules";
      setError(msg);
    } finally {
      setLoading(false);
    }
  }

  async function handleDelete(id) {
    setDeletingId(id);
    try {
      const json = await deleteChannelRule(id);
      if (!json.success) throw new Error(json.message || "Delete failed");
      setRules((prev) => prev.filter((r) => r._id !== id));
      toast.success("Channel rule deleted.");
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        "Delete failed";
      toast.error(`Delete failed: ${msg}`);
    } finally {
      setDeletingId(null);
    }
  }

  function renderCell(col, rule) {
    const val = rule[col.key];
    if (typeof val === "boolean") {
      return <span className={`font-medium ${val ? "text-green-500" : "text-red-500"}`}>{val ? "Yes" : "No"}</span>;
    }
    if (val == null || val === "") return <span className="text-gray-400">—</span>;
    return String(val);
  }

  return (
    <div className="bg-white rounded-3xl shadow-lg border border-gray-200  p-6 mb-6">
      <div className="flex flex-row justify-between items-start mb-8">
        <div className="flex flex-col gap-1">
          <h2 className="text-2xl font-bold text-gray-900">Channel Pricing Rules</h2>
          <p className="text-sm text-gray-600 mt-2">
            Costing Tool uses <strong>Web Retail</strong> &rarr;{" "}
            <strong>Fixed USD FX vs CAD</strong> to auto-calculate MSRP USD.
          </p>
        </div>

        <div className="flex items-center gap-4">
          <button
            onClick={fetchRules}
            disabled={loading}
            className="px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-2xl transition-all"
          >
            ↻ Refresh
          </button>
          <button
            onClick={() => setEditingRule({})}
            className="px-6 py-3 bg-black hover:bg-gray-800 text-white font-semibold rounded-2xl flex items-center gap-2 transition-all shadow-lg"
          >
            <Plus className="w-4 h-4" /> Add New Rule
          </button>
        </div>
      </div>

      {/* Fetch error banner */}
      {error && (
        <div className="mb-6 flex items-start gap-3 bg-red-50 border border-red-200 text-red-700 rounded-2xl px-5 py-4">
          <span className="text-lg leading-none mt-0.5">⚠</span>
          <div className="flex-1 text-sm font-medium">{error}</div>
          <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600 text-lg leading-none">×</button>
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto border border-gray-200 rounded-2xl">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-gray-700">
            <tr>
              {COLUMNS.map((col) => (
                <th key={col.key} className="px-6 py-4 text-left font-semibold">{col.label}</th>
              ))}
              <th className="px-6 py-4 text-center font-semibold">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={16} className="px-6 py-12 text-center text-gray-500">Loading...</td></tr>
            ) : rules.length === 0 ? (
              <tr><td colSpan={16} className="px-6 py-12 text-center text-gray-500">No rules found. Add one to get started.</td></tr>
            ) : (
              rules.map((rule) => (
                <tr key={rule._id} className="border-t border-gray-200 hover:bg-gray-50">
                  {COLUMNS.map((col) => (
                    <td key={col.key} className="px-6 py-4">{renderCell(col, rule)}</td>
                  ))}
                  <td className="px-6 py-4 text-center">
                    <div className="flex gap-4 justify-center">
                      <button
                        onClick={() => setEditingRule(rule)}
                        className="text-blue-600 hover:text-blue-700 font-medium px-3 py-1 rounded-lg hover:bg-blue-50"
                      >
                        <FaEdit />
                      </button>
                      <button
                        onClick={() => handleDelete(rule._id)}
                        disabled={deletingId === rule._id}
                        className="text-red-600 hover:bg-red-50 p-2 rounded-lg"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Modal — passes null for create, rule object for edit */}
      {editingRule !== null && (
        <ChannelRuleModal
          editingRule={Object.keys(editingRule).length ? editingRule : null}
          onClose={() => setEditingRule(null)}
          onSuccess={fetchRules}
        />
      )}
    </div>
  );
}